// src/services/oshaApi.ts - COMPLETE VERSION
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { 
  OMNISTACK_BASE_URL, 
  getPrepareHeaders 
} from '@/hooks/common/common-functions'

export const oshaApi = createApi({
  reducerPath: 'oshaApi',
  baseQuery: fetchBaseQuery({
    baseUrl: `${OMNISTACK_BASE_URL}osha`,
    prepareHeaders: (headers) => {
      // Add standard headers
      getPrepareHeaders({
        headers,
        isClientApiKey: true,
      });
      
      // Add business-specific API key
      const businessApiKey = localStorage.getItem('businessApiKey');
      if (businessApiKey) {
        headers.set('business-x-api-key', businessApiKey);
      }
      
      return headers;
    },
  }),
  tagTypes: ['OshaRequirements', 'OshaStats'],
  endpoints: (builder) => ({
    // Get OSHA requirements with filters
    getOshaRequirements: builder.query({
      query: (params) => ({
        url: '/requirements',
        params: {
          businessId: params.businessId,
          constructionSiteId: params.constructionSiteId,
          category: params.category,
          status: params.status,
          priority: params.priority,
          assignedTo: params.assignedTo,
          page: params.page || 1,
          limit: params.limit || 10
        }
      }),
      providesTags: ['OshaRequirements']
    }),
    
    // Get single OSHA requirement
    getOshaRequirement: builder.query({
      query: ({ id, businessId }) => ({
        url: `/requirements/${id}`,
        params: { businessId }
      }),
      //@ts-ignore
      providesTags: (result, error, { id }) => [{ type: 'OshaRequirements', id }]
    }),
    
    // Create OSHA requirement
    createOshaRequirement: builder.mutation({
      query: (requirementData) => ({
        url: '/requirements',
        method: 'POST',
        body: requirementData
      }),
      invalidatesTags: ['OshaRequirements', 'OshaStats']
    }),
    
    // Update OSHA requirement
    updateOshaRequirement: builder.mutation({
      query: ({ id, ...requirementData }) => ({
        url: `/requirements/${id}`,
        method: 'PUT',
        body: requirementData
      }),
      invalidatesTags: ['OshaRequirements', 'OshaStats']
    }),
    
    // Delete OSHA requirement
    deleteOshaRequirement: builder.mutation({
      query: ({ id, businessId }) => ({
        url: `/requirements/${id}`,
        method: 'DELETE',
        params: { businessId }
      }),
      invalidatesTags: ['OshaRequirements', 'OshaStats']
    }),

    // **NEW: Run OSHA Compliance Audit**
    runOshaAudit: builder.mutation({
      query: ({ businessId, constructionSiteId }) => ({
        url: '/audit',
        method: 'POST',
        body: { 
          businessId,
          constructionSiteId 
        }
      }),
      invalidatesTags: ['OshaRequirements', 'OshaStats']
    }),

    // **NEW: Get Upcoming Compliance Tasks**
    getUpcomingTasks: builder.query({
      query: ({ businessId, constructionSiteId, days = 30 }) => ({
        url: '/upcoming-tasks',
        params: {
          businessId,
          constructionSiteId,
          days
        }
      }),
      providesTags: ['OshaRequirements']
    }),

    // **NEW: Get Overdue Inspections**
    getOverdueInspections: builder.query({
      query: ({ businessId, constructionSiteId }) => ({
        url: '/overdue-inspections',
        params: {
          businessId,
          constructionSiteId
        }
      }),
      providesTags: ['OshaRequirements']
    }),
    
    // Get OSHA statistics
    getOshaStats: builder.query({
      query: (params) => ({
        url: '/stats',
        params: {
          businessId: params.businessId,
          constructionSiteId: params.constructionSiteId,
          dateFrom: params.dateFrom,
          dateTo: params.dateTo
        }
      }),
      providesTags: ['OshaStats']
    }),
    
    // Get OSHA summary
    getOshaSummary: builder.query({
      query: (businessId) => ({
        url: '/stats/summary',
        params: { businessId }
      }),
      providesTags: ['OshaStats']
    }),
    
    // Get OSHA trends
    getOshaTrends: builder.query({
      query: ({ businessId, period = '30d' }) => ({
        url: '/stats/trends',
        params: { businessId, period }
      }),
      providesTags: ['OshaStats']
    }),

    // **NEW: Get Upcoming Tasks Summary**
    getUpcomingTasksSummary: builder.query({
      query: ({ businessId, constructionSiteId }) => ({
        url: '/stats/upcoming-tasks-summary',
        params: {
          businessId,
          constructionSiteId
        }
      }),
      providesTags: ['OshaStats']
    }),

    // **NEW: Get Compliance Health Score**
    getComplianceHealthScore: builder.query({
      query: ({ businessId, constructionSiteId }) => ({
        url: '/stats/health-score',
        params: {
          businessId,
          constructionSiteId
        }
      }),
      providesTags: ['OshaStats']
    }),
    
    // Download PDF report
    downloadOshaReport: builder.mutation({
      query: (params) => ({
        url: '/reports/download',
        params: {
          businessId: params.businessId,
          constructionSiteId: params.constructionSiteId,
          reportType: params.reportType || 'summary',
          dateFrom: params.dateFrom,
          dateTo: params.dateTo
        },
        responseHandler: (response) => response.blob()
      })
    }),
    
    // Download Excel report
    downloadOshaExcel: builder.mutation({
      query: (params) => ({
        url: '/reports/excel',
        params: {
          businessId: params.businessId,
          constructionSiteId: params.constructionSiteId,
          includeInspections: params.includeInspections,
          includeViolations: params.includeViolations
        },
        responseHandler: (response) => response.blob()
      })
    }),
    
    // Preview report
    previewOshaReport: builder.query({
      query: (params) => ({
        url: '/reports/preview',
        params: {
          businessId: params.businessId,
          constructionSiteId: params.constructionSiteId,
          reportType: params.reportType || 'summary'
        }
      })
    })
  })
});

export const {
  useGetOshaRequirementsQuery,
  useGetOshaRequirementQuery,
  useCreateOshaRequirementMutation,
  useUpdateOshaRequirementMutation,
  useDeleteOshaRequirementMutation,
  useGetOshaStatsQuery,
  useGetOshaSummaryQuery,
  useGetOshaTrendsQuery,
  useDownloadOshaReportMutation,
  useDownloadOshaExcelMutation,
  usePreviewOshaReportQuery,
  useRunOshaAuditMutation,
  useGetUpcomingTasksQuery,
  useGetOverdueInspectionsQuery,
  useGetUpcomingTasksSummaryQuery,
  useGetComplianceHealthScoreQuery
} = oshaApi;